import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useLocation } from "@reach/router";
import { useStaticQuery, graphql } from "gatsby";

const Head = ({
  title,
  description,
  image,
  article = false,
  schema = null,
  robots = "index,follow",
  lang = "en",
  pathname: pathnameProp = null,
}) => {
  const location = useLocation();
  const pathname = pathnameProp || location.pathname || "/";

  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          defaultTitle
          defaultDescription
          siteUrl
          defaultImage
          author
          locale
        }
      }
    }
  `);

  const meta = site.siteMetadata;

  const pageTitle = title ? `${title} | ${meta.defaultTitle}` : meta.defaultTitle;

  const seo = {
    title: pageTitle,
    description: description || meta.defaultDescription,
    image: `${meta.siteUrl}${image || meta.defaultImage}`,
    url: `${meta.siteUrl}${pathname}`,
    author: meta.author || meta.defaultTitle,
    locale: meta.locale || "en_AU",
  };

  // Default JSON-LD: WebSite (safe, useful). Override via `schema` prop.
  const defaultSchema = schema
    ? schema
    : {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: meta.defaultTitle,
        url: meta.siteUrl,
        description: meta.defaultDescription,
        inLanguage: seo.locale,
      };

  return (
    <Helmet>
      <html lang={lang} />

      <title>{seo.title}</title>

      {/* Basic meta */}
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta name="author" content={seo.author} />
      <meta name="robots" content={robots} />

      {/* Canonical */}
      <link rel="canonical" href={seo.url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={meta.defaultTitle} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:locale" content={seo.locale} />
      <meta property="og:site_name" content={meta.defaultTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      {/* Verification */}
      <meta
        name="google-site-verification"
        content="DCl7VAf9tcz6eD9gb67NfkNnJ1PKRNcg8qQiwpbx9Lk"
      />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(defaultSchema)}</script>
    </Helmet>
  );
};

export default Head;

Head.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  article: PropTypes.bool,
  schema: PropTypes.object,
  robots: PropTypes.string,
  lang: PropTypes.string,
  pathname: PropTypes.string,
};

Head.defaultProps = {
  title: null,
  description: null,
  image: null,
  article: false,
  schema: null,
  robots: "index,follow",
  lang: "en",
  pathname: null,
};